import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiMoon, FiRefreshCw, FiShield, FiSun } from 'react-icons/fi';
import QRCode from 'qrcode';
import { cpApi } from '../api/api';
import { useTheme } from '../hooks/useTheme';
import VndPageLoading from '../components/VndPageLoading';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { getAutoRefreshInterval, isAutoRefreshEnabled, setAutoRefreshPreferences } from '../utils/preferences';
import type { Counterparty } from '../types';

const emitToast = (message: string, type: 'success' | 'error' = 'success') => {
  window.dispatchEvent(new CustomEvent('showToast', { detail: { message, type } }));
};

export default function VndSettingsPage() {
  const { t } = useTranslation();
  const { toggleTheme, isDark } = useTheme();
  const [displayName, setDisplayName] = useState('');
  const [login, setLogin] = useState('');
  const [profile, setProfile] = useState<Counterparty | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [themeSuccess, setThemeSuccess] = useState<string | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabledState] = useState(() => isAutoRefreshEnabled());
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(() => getAutoRefreshInterval());
  const [autoRefreshSaving, setAutoRefreshSaving] = useState(false);
  const [autoRefreshSuccess, setAutoRefreshSuccess] = useState<string | null>(null);
  const [twoFASetupOpen, setTwoFASetupOpen] = useState(false);
  const [twoFADisableOpen, setTwoFADisableOpen] = useState(false);
  const [twoFAQRCode, setTwoFAQRCode] = useState('');
  const [twoFABackupCodes, setTwoFABackupCodes] = useState<string[]>([]);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFADisableCode, setTwoFADisableCode] = useState('');
  const [twoFASaving, setTwoFASaving] = useState(false);
  const auto2FAStartRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const profile = await cpApi.getProfile();
        setProfile(profile);
        setDisplayName(profile.name || '');
        setLogin(profile.login || '');
      } catch {
        setError(t('settings.profileLoadError'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [t]);

  useEffect(() => {
    // UX: если 2FA обязательна, сразу открываем виджет настройки без скролла.
    if (!profile) return;
    if (profile.twofa_check) return;
    if (twoFASetupOpen || twoFADisableOpen) return;
    if (twoFASaving) return;
    if (auto2FAStartRef.current) return;

    auto2FAStartRef.current = true;
    void (async () => {
      try {
        setTwoFASaving(true);
        setError(null);
        setOk(null);
        const res = await cpApi.setup2FA();
        const data = res as { qr_code_url?: string; backup_codes?: string[] };
        setTwoFABackupCodes(data.backup_codes || []);
        setTwoFAQRCode(data.qr_code_url ? await QRCode.toDataURL(data.qr_code_url) : '');
        setTwoFACode('');
        setTwoFASetupOpen(true);
      } catch {
        // If auto-setup fails, the manual Enable button below remains available.
      } finally {
        setTwoFASaving(false);
      }
    })();
  }, [profile, twoFASetupOpen, twoFADisableOpen, twoFASaving]);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    setOk(null);
    try {
      await cpApi.updateProfile({
        name: displayName.trim() || undefined,
        login: login.trim() || undefined,
        password: newPassword.trim() || undefined,
      });
      setOk(t('settings.saved'));
      setNewPassword('');
    } catch {
      setError(t('settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleThemeToggle = () => {
    toggleTheme();
    const msg = t('settings.themeChanged');
    setThemeSuccess(msg);
    window.setTimeout(() => setThemeSuccess(null), 2000);
    emitToast(msg);
  };

  const handleAutoRefreshToggle = () => {
    const next = !autoRefreshEnabled;
    setAutoRefreshEnabledState(next);
    setAutoRefreshSaving(true);
    try {
      setAutoRefreshPreferences(next, autoRefreshInterval);
      setAutoRefreshSuccess(t('settings.autoRefreshSaved'));
      window.setTimeout(() => setAutoRefreshSuccess(null), 2000);
      emitToast(t('settings.autoRefreshToast'));
    } finally {
      setAutoRefreshSaving(false);
    }
  };

  const handleAutoRefreshIntervalChange = (value: number) => {
    const sanitized = Math.max(60, Math.min(300, Math.round(value)));
    setAutoRefreshInterval(sanitized);
  };

  const handleAutoRefreshApply = () => {
    setAutoRefreshSaving(true);
    try {
      setAutoRefreshPreferences(autoRefreshEnabled, autoRefreshInterval);
      setAutoRefreshSuccess(t('settings.autoRefreshSaved'));
      window.setTimeout(() => setAutoRefreshSuccess(null), 2000);
      emitToast(t('settings.intervalToast'));
    } finally {
      setAutoRefreshSaving(false);
    }
  };

  const start2FASetup = async () => {
    setTwoFASaving(true);
    setError(null);
    setOk(null);
    try {
      const res = await cpApi.setup2FA();
      const data = res as { qr_code_url?: string; backup_codes?: string[] };
      setTwoFABackupCodes(data.backup_codes || []);
      setTwoFAQRCode(data.qr_code_url ? await QRCode.toDataURL(data.qr_code_url) : '');
      setTwoFACode('');
      setTwoFASetupOpen(true);
    } catch {
      setError(t('settings.twoFaSetupStartError'));
    } finally {
      setTwoFASaving(false);
    }
  };

  const enable2FA = async () => {
    setTwoFASaving(true);
    setError(null);
    setOk(null);
    try {
      await cpApi.enable2FA(twoFACode.trim());
      const updated = await cpApi.getProfile();
      setProfile(updated);
      setTwoFASetupOpen(false);
      setTwoFACode('');
      setOk(t('settings.twoFaEnableSuccess'));
      emitToast(t('settings.twoFaEnableToast'));
      window.dispatchEvent(new Event('vndCpProfileRefresh'));
    } catch {
      setError(t('settings.twoFaWrongCode'));
    } finally {
      setTwoFASaving(false);
    }
  };

  const disable2FA = async () => {
    setTwoFASaving(true);
    setError(null);
    setOk(null);
    try {
      await cpApi.disable2FA(twoFADisableCode.trim());
      const updated = await cpApi.getProfile();
      setProfile(updated);
      setTwoFADisableOpen(false);
      setTwoFADisableCode('');
      setOk(t('settings.twoFaDisableSuccess'));
      emitToast(t('settings.twoFaDisableToast'));
      window.dispatchEvent(new Event('vndCpProfileRefresh'));
    } catch {
      setError(t('settings.twoFaWrongCode'));
    } finally {
      setTwoFASaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-3 sm:p-4 md:p-6">
        <VndPageLoading />
      </div>
    );
  }

  const autoIntervalHuman =
    autoRefreshInterval < 60
      ? t('settings.autoRefreshIntervalFmt', { n: autoRefreshInterval })
      : t('settings.autoRefreshIntervalFmtMin', { n: Math.round(autoRefreshInterval / 60) });

  return (
    <div className="w-full p-3 sm:p-4 md:p-6 max-w-none">
      {profile && !profile.twofa_check && (
        <div className="mb-6 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4 text-amber-800 dark:text-amber-200">
          <div className="font-semibold">{t('settings.twoFaBannerTitle')}</div>
          <div className="text-sm mt-1">{t('settings.twoFaBannerDesc')}</div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-4 shadow-sm lg:order-1">
        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('settings.primaryTitle')}</div>
        {error && <div className="text-sm text-red-500 dark:text-red-400">{error}</div>}
        {ok && <div className="text-sm text-green-600 dark:text-green-400">{ok}</div>}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('settings.displayNameLabel')}</div>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900/40 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('settings.loginLabel')}</div>
          <input
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900/40 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('settings.newPassword')}</div>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900/40 text-gray-900 dark:text-gray-100"
          />
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
        >
          {saving ? t('common.saving') : t('common.save')}
        </button>
        <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
          <LanguageSwitcher variant="settings" />
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm lg:order-3 lg:col-span-2">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center shrink-0">
            <FiRefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-300" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('settings.autoRefreshTitle')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('settings.autoRefreshDesc')}
            </p>
          </div>
        </div>
        {autoRefreshSuccess && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 rounded-lg text-sm">
            {autoRefreshSuccess}
          </div>
        )}
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{t('settings.autoRefreshLabel')}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {autoRefreshEnabled
                  ? t('settings.autoRefreshOn', { interval: autoIntervalHuman })
                  : t('settings.autoRefreshOff')}
              </p>
            </div>
            <button
              type="button"
              onClick={handleAutoRefreshToggle}
              disabled={autoRefreshSaving}
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                autoRefreshEnabled ? 'bg-blue-500 focus:ring-blue-500' : 'bg-gray-300 dark:bg-gray-600 focus:ring-gray-400'
              } ${autoRefreshSaving ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  autoRefreshEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className={`bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4 space-y-4 ${autoRefreshEnabled ? '' : 'opacity-50 pointer-events-none'}`}>
            <div className="flex items-center justify-between">
              <label htmlFor="vnd-auto-refresh-interval" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('settings.intervalLabel')}
              </label>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{Math.round(autoRefreshInterval / 60)}</span>
            </div>
            <input
              id="vnd-auto-refresh-interval"
              type="range"
              min={60}
              max={300}
              step={60}
              value={autoRefreshInterval}
              onChange={(e) => handleAutoRefreshIntervalChange(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>{t('settings.intervalMin')}</span>
              <span>{t('settings.intervalMax')}</span>
            </div>
            <button
              type="button"
              onClick={handleAutoRefreshApply}
              disabled={autoRefreshSaving}
              className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {autoRefreshSaving ? t('common.saving') : t('settings.saveSettingsBtn')}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm lg:order-2">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center shrink-0">
            {isDark ? (
              <FiSun className="w-5 h-5 text-indigo-600 dark:text-indigo-300" />
            ) : (
              <FiMoon className="w-5 h-5 text-indigo-600 dark:text-indigo-300" />
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('settings.themeTitle')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('settings.themeDesc')}
            </p>
          </div>
        </div>
        {themeSuccess && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 rounded-lg text-sm">
            {themeSuccess}
          </div>
        )}
        <div className="flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {isDark ? t('settings.themeDark') : t('settings.themeLight')}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {isDark ? t('settings.themeDarkHint') : t('settings.themeLightHint')}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isDark}
            aria-label={isDark ? t('settings.themeAriaDark') : t('settings.themeAriaLight')}
            onClick={handleThemeToggle}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
              isDark ? 'bg-indigo-500 focus:ring-indigo-500' : 'bg-gray-300 dark:bg-gray-600 focus:ring-gray-400'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                isDark ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm lg:order-4 lg:col-span-2">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center shrink-0">
            <FiShield className="w-5 h-5 text-emerald-600 dark:text-emerald-300" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('settings.twoFaTitle')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {profile?.twofa_check ? t('settings.twoFaSubtitleOn') : t('settings.twoFaSubtitleOff')}
            </p>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {profile?.twofa_check ? t('settings.twoFaStatusOn') : t('settings.twoFaStatusOff')}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{t('settings.twoFaAppHint')}</div>
            </div>
            <button
              type="button"
              onClick={profile?.twofa_check ? () => setTwoFADisableOpen(true) : start2FASetup}
              disabled={twoFASaving}
              className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${profile?.twofa_check ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            >
              {twoFASaving ? t('settings.twoFaWait') : profile?.twofa_check ? t('settings.twoFaDisable') : t('settings.twoFaEnable')}
            </button>
          </div>
        </div>
      </div>
      </div>

      {twoFASetupOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-4">
              <div className="font-semibold text-gray-900 dark:text-gray-100">{t('settings.twoFaSetupTitle')}</div>
              <button
                type="button"
                onClick={() => setTwoFASetupOpen(false)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {t('common.close')}
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-gray-700 dark:text-gray-300">{t('settings.twoFaSetupIntro')}</div>
              {twoFAQRCode && (
                <div className="flex justify-center">
                  <img src={twoFAQRCode} alt={t('settings.twoFaQrAlt')} className="w-56 h-56 bg-white rounded-xl p-3" />
                </div>
              )}
              {twoFABackupCodes.length > 0 && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('settings.twoFaBackupTitle')}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {twoFABackupCodes.map((code) => (
                      <div key={code} className="font-mono text-sm px-2 py-1 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                        {code}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <input
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder={t('settings.twoFaCodePlaceholder')}
                  inputMode="numeric"
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100"
                />
                <button
                  type="button"
                  onClick={enable2FA}
                  disabled={twoFASaving || twoFACode.length !== 6}
                  className="px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                >
                  {t('settings.twoFaConfirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {twoFADisableOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-4">
              <div className="font-semibold text-gray-900 dark:text-gray-100">{t('settings.twoFaDisableTitle')}</div>
              <button
                type="button"
                onClick={() => setTwoFADisableOpen(false)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {t('common.close')}
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-gray-700 dark:text-gray-300">{t('settings.twoFaDisableIntro')}</div>
              <input
                value={twoFADisableCode}
                onChange={(e) => setTwoFADisableCode(e.target.value.replace(/[^0-9A-Za-z-]/g, '').slice(0, 16))}
                placeholder={t('settings.twoFaDisableCodePlaceholder')}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100"
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setTwoFADisableOpen(false)}
                  className="px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={disable2FA}
                  disabled={twoFASaving || twoFADisableCode.trim().length < 6}
                  className="px-4 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  {t('settings.twoFaDisableSubmit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

